import React, { useEffect, useState, useRef } from "react";
import "./ContactForm.css";
import { toast } from "react-toastify";
import axios from "axios";
import API_BASE_URL from "../../../config/api";

const ContactForm = () => {
  const [FormData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  // 👇 ref for the typewriter heading
  const titleRef = useRef(null);

  const handleData = (e) => {
    const { name, value } = e.target;
    setFormData({ ...FormData, [name]: value });
  };

  const validateName = (name) => /^[a-zA-Z\s]{2,50}$/.test(name);
  const validateEmail = (email) =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  const validatePhone = (phone) => /^\+?[0-9]{10,15}$/.test(phone);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!FormData.name || !FormData.email || !FormData.phone || !FormData.message) {
      toast.error("Please fill all the fields");
      return;
    }
    if (!validateName(FormData.name)) {
      toast.error("Please enter a valid name!");
      return;
    }
    if (!validateEmail(FormData.email)) {
      toast.error("Please enter a valid email address!");
      return;
    }
    if (!validatePhone(FormData.phone)) {
      toast.error("Please enter a valid phone number!");
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/contact.php`,
        FormData,
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        }
      );
      if (response.status === 200) {
        setFormData({ name: "", email: "", phone: "", message: "" });
        toast.success("Form submitted successfully");
      } else {
        toast.error("Submission failed. Please try again.");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || "An error occurred. Please try again.";
      toast.error(`Error: ${errorMsg}`);
      console.error("Error details:", error);
    }
  };

  // floating label focus/blur behaviour
  useEffect(() => {
    const inputs = document.querySelectorAll(".input");
    function focusFunc() {
      this.parentNode.classList.add("focus");
    }
    function blurFunc() {
      if (this.value === "") this.parentNode.classList.remove("focus");
    }
    inputs.forEach((input) => {
      input.addEventListener("focus", focusFunc);
      input.addEventListener("blur", blurFunc);
    });
    return () => {
      inputs.forEach((input) => {
        input.removeEventListener("focus", focusFunc);
        input.removeEventListener("blur", blurFunc);
      });
    };
  }, []);

  // 👇 Start typewriter animation only when heading is in viewport
  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add("start-typing");
            observer.unobserve(el); // run only once
          }
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="container-fluid contact-container" id="contactusform">
      <span className="big-circle" />
      <img src="img/shape.png" className="square" alt="" />

      <div className="contacts-form row">
        {/* LEFT SIDE */}
        <div className="contact-info col-md-6">
          <h3 ref={titleRef} className="title typewriter">
            Let&apos;s get in <span>touch</span>
          </h3>
          <p className="text">
            NexTech is a top PLM and Engineering solutions provider, empowering
            global customers in digital transformation with Teamcenter PLM as
            their core platform.
          </p>

          <div className="info">
            <div className="information">
              <i className="fa-solid fa-envelope fa-beat"></i> &nbsp;&nbsp;
              <p>
                <a
                  href="mailto:info@nextechplm.com"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  info@nextechplm.com
                </a>
              </p>
            </div>

            <div className="information">
              <i className="fa-solid fa-location-dot fa-beat"></i> &nbsp;&nbsp;
              <p>
                <a
                  href="https://maps.app.goo.gl/nPjk9XgHVn8Jqfyq7"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  NexTech PLM Solution pvt.ltd, 2nd floor, The Capitol, New DP
                  Rd, Vishal Nagar, Pimple Nilakh, Pune, Pimpri-Chinchwad,
                  Maharashtra 411027
                </a>
              </p>
            </div>

            <div className="information">
              <i className="fa-solid fa-location-dot fa-beat"></i> &nbsp;&nbsp;
              <p>
                <a
                  href="https://www.google.com/maps?q=4th+Floor,+R-5+Heights,+27,+Vishal+Nagar,+Pimple+Nilakh,+Pimpri+Chinchwad,+Pune+411027"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  635 W 42nd St Suite 37E, New York, NY 10036, USA
                </a>
              </p>
            </div>

            <div className="information">
              <i className="fa-solid fa-location-dot fa-beat"></i> &nbsp;&nbsp;
              <p>
                <a
                  href="https://www.google.com/maps?q=4th+Floor,+R-5+Heights,+27,+Vishal+Nagar,+Pimple+Nilakh,+Pimpri+Chinchwad,+Pune+411027"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  11 rue des Draperies, 69450 St-Cyr-au-Mont-d'Or, France
                </a>
              </p>
            </div>
          </div>

          <div className="social-media">
            <p>Connect with us :</p>
            <div className="social-icons">
              <a
                href="https://www.linkedin.com/company/nextech-plm-solutions/"
                className="linkedin"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa-brands fa-linkedin"></i>
              </a>
              <a
                href="https://www.facebook.com/share/vNf8TQB9UWD12RVY/?mibextid=qi2Omg"
                className="facebook"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa-brands fa-facebook-f"></i>
              </a>
              <a
                href="https://twitter.com/NextechPlm"
                className="twitter"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa-brands fa-x-twitter"></i>
              </a>
              <a
                href="https://www.instagram.com/nextech_plm_solutions?igsh=MTUwOWtvM3h2cHJrdw=="
                className="instagram"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa-brands fa-instagram"></i>
              </a>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="contact-form col-md-6">
          <span className="circle one"></span>
          <span className="circle two"></span>

          <form onSubmit={handleSubmit} autoComplete="off">
            <h3 className="title form-title">Contact us</h3>

            <div className="contact-input-container">
              <input
                type="text"
                name="name"
                className="input"
                onChange={handleData}
                value={FormData.name}
              />
              <label htmlFor="name">Name</label>
              <span>Name</span>
            </div>

            <div className="contact-input-container">
              <input
                type="email"
                name="email"
                className="input"
                onChange={handleData}
                value={FormData.email}
              />
              <label htmlFor="email">Email</label>
              <span>Email</span>
            </div>

            <div className="contact-input-container">
              <input
                type="tel"
                name="phone"
                className="input"
                onChange={handleData}
                value={FormData.phone}
              />
              <label htmlFor="phone">Phone</label>
              <span>Phone</span>
            </div>

            <div className="contact-input-container textarea">
              <textarea
                name="message"
                className="input"
                onChange={handleData}
                value={FormData.message}
              />
              <label htmlFor="message">Message</label>
              <span>Message</span>
            </div>

            <input type="submit" value="Submit" className="btn" />
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
